import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import MoneyUtils from '../../../utils/moneyUtils';
import { capitalizeFirst } from '../../../utils/stringUtils';

const PreOrderDetailsSheet = ({ isOpen, onClose, cart, theme }) => {
  if (!cart) return null;

  const brandColor = theme?.secondary || '#f97316'; // orange-500 default
  const brandTextColor = theme?.primary || '#fff';

  const { items, itemTotal, taxesAndCharges, finalAmount, taxSummaries, chargeSummaries, code } = cart;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
          />

          {/* Sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            className="fixed bottom-0 left-0 right-0 bg-white rounded-t-[40px] z-[101] max-h-[90vh] overflow-hidden flex flex-col"
          >
            {/* Handle */}
            <div className="flex justify-center p-4">
              <div className="w-12 h-1.5 bg-gray-200 rounded-full" />
            </div>

            {/* Header */}
            <div className="px-6 pb-4 border-b border-gray-50 flex items-center justify-between">
              <div>
                <h2 className="text-xl font-black text-gray-900">Pre-order Details</h2>
                <p className="text-xs text-gray-400 font-bold uppercase tracking-widest mt-1">
                  Pickup Code: <span style={{ color: brandColor }}>{code}</span>
                </p>
              </div>
              <button
                onClick={onClose}
                className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4">
              {/* Items List */}
              <div className="space-y-4 mb-8">
                <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 mb-4">Items</h3>
                {items.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-black text-gray-900">{item.quantity}x</span>
                        <span className="font-bold text-gray-800">{capitalizeFirst(item.name)}</span>
                      </div>
                      {item.portion && (
                        <p className="text-[10px] text-gray-400 font-bold ml-7">
                          {capitalizeFirst(item.portion.name)}
                        </p>
                      )}
                      {item.addons && item.addons.length > 0 && (
                        <p className="text-[10px] text-gray-400 font-bold ml-7">
                          + {item.addons.map(a => capitalizeFirst(a.name)).join(', ')}
                        </p>
                      )}
                    </div>
                    <span className="font-black text-gray-900">
                      {MoneyUtils.format(item.price * item.quantity)}
                    </span>
                  </div>
                ))}
              </div>

              {/* Billing Summary */}
              <div className="space-y-3 bg-gray-50 p-6 rounded-[32px] border border-gray-100">
                <h3 className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 mb-4">Billing Summary</h3>
                
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500 font-bold">Item Total</span>
                  <span className="text-gray-900 font-black">{MoneyUtils.format(itemTotal)}</span>
                </div>

                {/* Taxes Breakdown */}
                {taxSummaries && taxSummaries.length > 0 && (
                  <div className="pt-2 space-y-2">
                    {taxSummaries.map((tax, idx) => (
                      <div key={idx} className="flex justify-between text-[11px]">
                        <span className="text-gray-400 font-bold italic">{tax.taxName || tax.name}</span>
                        <span className="text-gray-600 font-bold">{MoneyUtils.format(tax.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Charges Breakdown */}
                {chargeSummaries && chargeSummaries.length > 0 && (
                  <div className="pt-2 space-y-2">
                    {chargeSummaries.map((charge, idx) => (
                      <div key={idx} className="flex justify-between text-[11px]">
                        <span className="text-gray-400 font-bold italic">{charge.chargeName || charge.name}</span>
                        <span className="text-gray-600 font-bold">{MoneyUtils.format(charge.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="pt-4 mt-2 border-t border-gray-200 flex justify-between items-center">
                  <span className="text-base font-black text-gray-900 uppercase tracking-tight">Grand Total</span>
                  <span className="text-2xl font-black text-gray-900 tracking-tighter">
                    {MoneyUtils.format(finalAmount)}
                  </span>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 bg-white border-t border-gray-50">
              <button
                onClick={onClose}
                className="w-full py-4 rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl active:scale-[0.98] transition-all"
                style={{ backgroundColor: brandColor, color: brandTextColor, boxShadow: `0 10px 25px -5px ${brandColor}40` }}
              >
                Got It
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default PreOrderDetailsSheet;
